import { useAuth0 } from '@auth0/auth0-react';
import {
  GenerateTodayUpdateResponse,
  GetTodaySettingsResponse,
  GetTodayUpdateResponse,
  TodayEvents,
  TodaySettings,
  TodayUpdate,
  type TodayUpdateGeneratedPayload,
} from '@dreamai/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api';
import { useAgent } from '../../contexts/AgentContext';
import { usePusher } from '../../contexts/PusherContext';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as CloseXIcon } from '../../icons/close-x.svg';
import { ReactComponent as EditIcon } from '../../icons/edit.svg';
import { ReactComponent as RefreshIcon } from '../../icons/refresh-arrows.svg';
import { ReactComponent as ShareIcon } from '../../icons/share.svg';
import { ReactComponent as WritingPencilIcon } from '../../icons/writing-pencil.svg';
import { formatPostedLabelForDay } from '../../utils/formatPostedLabelForDay';
import ErrorState from '../common/ErrorState';
import MobileNavToggle from './MobileNavToggle';
import './today-page.scss';
import TodaySettingsModal from './TodaySettingsModal';

const formatHeaderDate = (date: Date): string =>
  date
    .toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
    .toUpperCase()
    .replace(',', '');

const formatNavDate = (date: Date): string =>
  date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const toDateStr = (date: Date): string => date.toISOString().slice(0, 10);

const TodayPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { agents } = useAgent();
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;

  const primaryAgent = useMemo(
    () => agents.find((a) => (a.agent_type as string) === 'Personal Assistant'),
    [agents],
  );
  const agentNickname =
    primaryAgent?.nickname || primaryAgent?.name || 'Secretary';

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [update, setUpdate] = useState<TodayUpdate | null>(null);
  const [settings, setSettings] = useState<TodaySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [earliestDate, setEarliestDate] = useState<string | null>(null);
  const [myDayModalOpen, setMyDayModalOpen] = useState(false);
  const [myDayDate, setMyDayDate] = useState<Date>(new Date());
  const [myDayUpdate, setMyDayUpdate] = useState<TodayUpdate | null>(null);
  const [myDayLoading, setMyDayLoading] = useState(false);

  const isToday = toDateStr(selectedDate) === toDateStr(new Date());
  const isEarliestDay =
    earliestDate == null || toDateStr(selectedDate) <= earliestDate;

  // Fetch update for selected date
  const fetchUpdate = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const dateStr = toDateStr(selectedDate);
      const res = await api.get<GetTodayUpdateResponse>(
        `/today/updates/${dateStr}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        setUpdate(res.data.update);
        setGenerating(res.data.update?.status === 'generating');
        setFetchError(false);
      }
    } catch (err) {
      console.error('Error fetching today update:', err);
      setUpdate(null);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, getAccessTokenSilently]);

  // Fetch settings on mount
  const fetchSettings = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetTodaySettingsResponse>('/today/settings', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setSettings(res.data.settings);
      }
    } catch (err) {
      console.error('Error fetching today settings:', err);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchUpdate();
  }, [fetchUpdate]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Fetch earliest update date on mount
  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<{
          success: boolean;
          earliestDate: string | null;
        }>('/today/earliest-date', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) {
          setEarliestDate(res.data.earliestDate);
        }
      } catch (err) {
        console.error('Error fetching earliest date:', err);
      }
    })();
  }, [getAccessTokenSilently]);

  // Listen for Pusher event when generation completes (user channel)
  useEffect(() => {
    if (!userChannel) return;

    const handleTodayUpdateGenerated = (data: TodayUpdateGeneratedPayload) => {
      if (data.date === toDateStr(selectedDate)) {
        fetchUpdate();
      }
    };

    userChannel.bind(TodayEvents.UPDATE_GENERATED, handleTodayUpdateGenerated);
    return () => {
      userChannel.unbind(
        TodayEvents.UPDATE_GENERATED,
        handleTodayUpdateGenerated,
      );
    };
  }, [userChannel, selectedDate, fetchUpdate]);

  // Stop generating state once update is no longer 'generating'
  useEffect(() => {
    if (update && update.status !== 'generating') {
      setGenerating(false);
    }
  }, [update]);

  const handlePrevDay = () => {
    if (isEarliestDay) return;
    setSelectedDate((d) => {
      const prev = new Date(d);
      prev.setDate(prev.getDate() - 1);
      return prev;
    });
  };

  const handleNextDay = () => {
    if (isToday) return;
    setSelectedDate((d) => {
      const next = new Date(d);
      next.setDate(next.getDate() + 1);
      return next;
    });
  };

  const handleRegenerate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.post<GenerateTodayUpdateResponse>(
        '/today/generate',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success && res.data.updateId) {
        setUpdate((prev) =>
          prev ? { ...prev, status: 'generating', summaryHtml: null } : null,
        );
      }
    } catch (err) {
      console.error('Error triggering regeneration:', err);
      setGenerating(false);
    }
  };

  const handleShare = async (
    updateId: string,
    imageId: string,
    caption: string,
  ) => {
    const shareUrl = `${process.env.REACT_APP_API_URL}/today/updates/shared/${updateId}/${imageId}/meta`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Today Update',
          text: caption,
          url: shareUrl,
        });
      } catch {
        // User cancelled or share failed
      }
    }
  };

  const handleSettingsSaved = () => {
    fetchSettings();
  };

  // Fetch update for the My Day modal's independent date
  const fetchMyDayUpdate = useCallback(
    async (date: Date) => {
      setMyDayLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const dateStr = toDateStr(date);
        const res = await api.get<GetTodayUpdateResponse>(
          `/today/updates/${dateStr}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data.success) {
          setMyDayUpdate(res.data.update);
        }
      } catch (err) {
        console.error('Error fetching my day update:', err);
        setMyDayUpdate(null);
      } finally {
        setMyDayLoading(false);
      }
    },
    [getAccessTokenSilently],
  );

  const handleOpenMyDay = () => {
    setMyDayDate(new Date());
    setMyDayModalOpen(true);
    fetchMyDayUpdate(new Date());
  };

  const handleMyDayPrev = () => {
    const isMyDayEarliest =
      earliestDate == null || toDateStr(myDayDate) <= earliestDate;
    if (isMyDayEarliest) return;
    setMyDayDate((d) => {
      const prev = new Date(d);
      prev.setDate(prev.getDate() - 1);
      fetchMyDayUpdate(prev);
      return prev;
    });
  };

  const handleMyDayNext = () => {
    const isMyDayToday = toDateStr(myDayDate) === toDateStr(new Date());
    if (isMyDayToday) return;
    setMyDayDate((d) => {
      const next = new Date(d);
      next.setDate(next.getDate() + 1);
      fetchMyDayUpdate(next);
      return next;
    });
  };

  const myDayIsToday = toDateStr(myDayDate) === toDateStr(new Date());
  const myDayIsEarliest =
    earliestDate == null || toDateStr(myDayDate) <= earliestDate;
  const myDayImages = myDayUpdate?.images || [];

  return (
    <div className='today-page inset-white-backed'>
      <MobileNavToggle />
      {/* Page header */}
      <div className='header-wrapper'>
        <div className='top-left'>
          <p className='date'>{formatHeaderDate(selectedDate)}</p>
          <h2>What's on Today</h2>
        </div>
        {primaryAgent?.image_url && (
          <button className='my-day-button' onClick={handleOpenMyDay}>
            <div className='my-day-avatar'>
              <img src={primaryAgent.image_url} alt={agentNickname} />
            </div>
            <span>My day</span>
          </button>
        )}
      </div>

      {/* Main content */}
      <div className='today-content'>
        {/* Update section */}
        <div className='update-section'>
          <div className='update-header'>
            <div className='nav-group'>
              <button
                className='nav-button'
                onClick={handlePrevDay}
                disabled={isEarliestDay}
                title='Previous day'
              >
                <ArrowLeftIcon />
              </button>

              <span className='current-date'>
                {formatNavDate(selectedDate)}
              </span>

              <button
                className='nav-button forward'
                onClick={handleNextDay}
                disabled={isToday}
                title='Next day'
              >
                <ArrowLeftIcon />
              </button>
            </div>

            <div className='actions-group'>
              {isToday && (
                <button
                  className={`regenerate-button${generating ? ' spinning' : ''}`}
                  onClick={handleRegenerate}
                  disabled={generating}
                  title='Regenerate update'
                >
                  <RefreshIcon />
                </button>
              )}

              <button
                className='customise-button'
                onClick={() => setSettingsModalOpen(true)}
              >
                <EditIcon />
                <span>Customise</span>
              </button>
            </div>
          </div>

          <div className='update-body'>
            {fetchError ? (
              <ErrorState
                title='Unable to load briefing'
                subtitle='Something’s gone wrong, try refreshing the page.'
              />
            ) : loading ? (
              <div className='loading-state'>
                <div className='skeleton-line long' />
                <div className='skeleton-line medium' />
                <div className='skeleton-line long' />
                <div className='skeleton-line short' />
              </div>
            ) : generating ? (
              <div className='generating-state'>
                <WritingPencilIcon />
                <p>Writing your briefing</p>
                <small>
                  Depending on your instructions this can take a few minutes.
                  You can check back in soon
                </small>
              </div>
            ) : update?.status === 'failed' ? (
              <div className='failed-state'>
                <ErrorState
                  title='Generation failed'
                  subtitle='Something went wrong while writing your briefing. Please try again.'
                />
                <button className='generate-button' onClick={handleRegenerate}>
                  Try again
                </button>
              </div>
            ) : update?.summaryHtml ? (
              <div
                className='summary-html'
                dangerouslySetInnerHTML={{ __html: update.summaryHtml }}
              />
            ) : (
              <div className='empty-state'>
                {isToday ? (
                  <>
                    <p>No update for today yet.</p>
                    <button
                      className='generate-button'
                      onClick={handleRegenerate}
                    >
                      Generate now
                    </button>
                  </>
                ) : (
                  <p>No update for this day.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Instagram images section */}
        {(generating || fetchError) &&
          (!update?.images || update.images.length === 0) && (
            <div className='images-section'>
              <div className='images-scroll'>
                <div className='image-card ghost-card'>
                  <div className='ghost-image' />
                  <div className='image-caption-row'>
                    <div className='ghost-avatar' />
                    <div className='ghost-texts'>
                      <div className='ghost-line' style={{ width: 91 }} />
                      <div className='ghost-line' style={{ width: 64 }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        {!fetchError && update?.images && update.images.length > 0 && (
          <div className='images-section'>
            <div className='images-scroll'>
              {update.images.map((img) => (
                <div key={img.id} className='image-card'>
                  {img.imageUrl && (
                    <img
                      src={img.imageUrl}
                      alt={img.caption || 'Today update image'}
                      className='update-image'
                    />
                  )}
                  {img.caption && (
                    <div className='image-caption-row'>
                      {primaryAgent?.image_url && (
                        <div className='dreamie-image-wrapper'>
                          <img
                            src={primaryAgent.image_url}
                            alt={agentNickname}
                          />
                        </div>
                      )}
                      <div className='caption-content'>
                        <p className='caption-text'>
                          <b>{agentNickname}</b> {img.caption}
                        </p>
                        <span className='caption-time'>
                          {formatPostedLabelForDay(img.createdAt)}
                        </span>
                      </div>
                      {img.imageUrl && update && (
                        <button
                          className='share-button'
                          onClick={() =>
                            handleShare(update.id, img.id, img.caption || '')
                          }
                        >
                          <ShareIcon />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* My Day modal (mobile) */}
      {myDayModalOpen && (
        <div
          className='my-day-modal-overlay'
          onClick={() => setMyDayModalOpen(false)}
        >
          <div className='my-day-modal' onClick={(e) => e.stopPropagation()}>
            <div className='my-day-modal-header'>
              <div className='my-day-header-pill'>
                <button
                  className='nav-button'
                  onClick={handleMyDayPrev}
                  disabled={myDayIsEarliest}
                >
                  <ArrowLeftIcon />
                </button>
                <span className='my-day-date'>{formatNavDate(myDayDate)}</span>
                <button
                  className='nav-button forward'
                  onClick={handleMyDayNext}
                  disabled={myDayIsToday}
                >
                  <ArrowLeftIcon />
                </button>
                <button
                  className='my-day-close'
                  onClick={() => setMyDayModalOpen(false)}
                >
                  <CloseXIcon />
                </button>
              </div>
            </div>
            <div className='my-day-modal-body'>
              {myDayLoading ? (
                <div className='my-day-loading'>
                  <div className='image-card ghost-card'>
                    <div className='ghost-image' />
                    <div className='image-caption-row'>
                      <div className='caption-left'>
                        <div className='ghost-avatar' />
                        <div className='ghost-texts'>
                          <div className='ghost-line' style={{ width: 91 }} />
                          <div className='ghost-line' style={{ width: 64 }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : myDayImages.length > 0 ? (
                myDayImages.map((img) => (
                  <div key={img.id} className='image-card'>
                    {img.imageUrl && (
                      <div className='image-frame'>
                        <img
                          src={img.imageUrl}
                          alt={img.caption || 'Today update image'}
                          className='update-image'
                          onLoad={(e) =>
                            (e.target as HTMLImageElement).classList.add(
                              'loaded',
                            )
                          }
                        />
                      </div>
                    )}
                    <div className='image-caption-row'>
                      <div className='caption-left'>
                        {primaryAgent?.image_url && (
                          <div className='dreamie-image-wrapper'>
                            <img
                              src={primaryAgent.image_url}
                              alt={agentNickname}
                            />
                          </div>
                        )}
                        <div className='caption-content'>
                          <p className='caption-text'>
                            <b>{agentNickname}</b> {img.caption || ''}
                          </p>
                          <span className='caption-time'>
                            {formatPostedLabelForDay(img.createdAt)}
                          </span>
                        </div>
                      </div>
                      <div className='caption-actions'>
                        {img.imageUrl && myDayUpdate && (
                          <button
                            className='share-button'
                            onClick={() =>
                              handleShare(
                                myDayUpdate.id,
                                img.id,
                                img.caption || '',
                              )
                            }
                          >
                            <ShareIcon />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className='my-day-empty'>
                  <p>No images for this day.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      <TodaySettingsModal
        isOpen={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        settings={settings}
        onSaved={handleSettingsSaved}
      />
    </div>
  );
};

export default TodayPage;
